import CardAction from "../UI/CardAction";
import {
  IconCalendarCheck,
  IconClipboardCheck,
  IconFilePlus,
  IconFileText,
  IconReceipt,
  IconRefresh,
  IconScale,
  IconSend,
  IconTrendingUp,
  IconUserShield,
} from "@tabler/icons-react";
import styles from "./ActionsCourriers.module.css";
import { useModal } from "../../hooks/use-modal.jsx";
import { classnames } from "../../utils/index.jsx";
import BulkQuittanceModal from "./Bulk/BulkQuittanceModal";
import { useEffect, useState } from "react";
import useTenants from "../../hooks/use-tenants";
import AvenantForm from "./Actions/AvenantForm";
import TenantAddForm from "./Forms/TenantAddForm";
import EdlRealisation from "../Edl/EdlRealisation";
import CautionForm from "./Actions/CautionForm";
import { useSearchParams } from "react-router-dom";
import MandatForm from "./Forms/MandatForm";
import AttestationCafModal from "./AttestationCafModal";
import { ChargesType, TenantLeaseType } from "../../utils/constants/tenants.js";

export default function ActionsCourriers({
  tenant,
  property,
  balances,
  updatePropertyCles,
}) {
  const [isQuittanceModalOpen, setIsQuittanceModalOpen] = useState(false);
  const [isAvenantOpen, setIsAvenantOpen] = useState(false);
  const [CautionModal, , openCautionModal, isCautionModalReady] = useModal();
  const [edlOpen, setEdlOpen] = useState(false);
  const [MandatModal, closeMandatModal, openMandatModal] = useModal();

  const [searchParams, setSearchParams] = useSearchParams();
  const [LeaseRegenerateFormModal, , openLeaseRegenerateFormModal] = useModal();
  const [typeCourrier, setTypeCourrier] = useState();
  const [shortcut, setShortcut] = useState();
  const [isAttestationCafOpen, setIsAttestationCafOpen] = useState(false);

  const { createCourrier } = useTenants();

  useEffect(() => {
    if (searchParams.get("action") === "avenant") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTypeCourrier("avenant");
      setShortcut(searchParams.get("avenant-type"));
      setSearchParams((params) => {
        const newParams = new URLSearchParams(params);
        newParams.delete("action");
        newParams.delete("avenant-type");
        return newParams;
      });
      setIsAvenantOpen(true);
    } else if (searchParams.get("action") === "courrier") {
      setTypeCourrier("courrier");
      setShortcut(searchParams.get("avenant-type"));
      setSearchParams((params) => {
        const newParams = new URLSearchParams(params);
        newParams.delete("action");
        newParams.delete("avenant-type");
        return newParams;
      });
      setIsAvenantOpen(true);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!isCautionModalReady || searchParams.get("action") !== "caution") {
      return;
    }
    setSearchParams((params) => {
      const newParams = new URLSearchParams(params);
      newParams.delete("action");
      return newParams;
    });
    openCautionModal();
  }, [isCautionModalReady, openCautionModal, searchParams, setSearchParams]);

  return (
    <>
      <MandatForm
        tenantId={tenant.id}
        property={property}
        modal={MandatModal}
        onSubmit={() => closeMandatModal()}
      />
      <BulkQuittanceModal
        open={isQuittanceModalOpen}
        onOpenChange={setIsQuittanceModalOpen}
        lockedTenantId={tenant?.id}
      />
      <CautionForm tenant={tenant} Modal={CautionModal} />
      <AvenantForm
        balances={balances}
        open={isAvenantOpen}
        onOpenChange={setIsAvenantOpen}
        tenant={tenant}
        onSubmit={createCourrier}
        type={typeCourrier}
        shortcut={shortcut}
        resetShortcut={() => setShortcut("")}
        property={property}
      />
      <TenantAddForm
        isRegenerate
        modal={LeaseRegenerateFormModal}
        tenant={tenant}
      />

      <EdlRealisation
        tenant={tenant}
        property={property}
        open={edlOpen}
        onOpenChange={setEdlOpen}
        onSubmitted={() => setEdlOpen(false)}
        updatePropertyCles={updatePropertyCles}
      />
      <AttestationCafModal
        open={isAttestationCafOpen}
        onOpenChange={setIsAttestationCafOpen}
        tenantId={tenant.id}
        tenantName={tenant.nom}
        isColoc={!!property?.is_coloc}
      />
      <div className={classnames(styles.grid, styles.stroke)}>
        <CardAction
          icon={IconReceipt}
          iconColor="info"
          description={
            "Envoyez une quittance à votre locataire pour attester du paiement du loyer"
          }
          title={"Générer une quittance"}
          onClick={() => setIsQuittanceModalOpen(true)}
          requiresWrite
        />
        <CardAction
          icon={IconTrendingUp}
          iconColor="orange"
          description={
            "Indexez votre loyer sur l'indice de l'INSEE prévu par la clause de révision de votre contrat de bail"
          }
          title={"Faire une révision de loyer"}
          onClick={() => {
            setTypeCourrier("courrier");
            setShortcut("courrier_irl");
            setIsAvenantOpen(true);
          }}
          requiresWrite
        />
        <CardAction
          icon={IconClipboardCheck}
          iconColor="success"
          description={
            "Réaliser l'état des lieux d'entrée ou l'état des lieux de sortie de votre logement."
          }
          title={"Réaliser un état des lieux"}
          onClick={() => setEdlOpen(true)}
          requiresWrite
        />
        <CardAction
          icon={IconFilePlus}
          iconColor="violet"
          description={
            "Envoyer un avenant au bail à votre locataire (changement de locataire, modification du loyer ou des charges, etc.)"
          }
          title={"Faire un avenant au bail"}
          onClick={() => {
            setTypeCourrier("avenant");
            setIsAvenantOpen(true);
          }}
          requiresWrite
        />
        <CardAction
          icon={IconRefresh}
          iconColor="info"
          description={
            "Permet de mettre à jour le document de bail, si vous modifiez un paramètre après sa génération."
          }
          title={"Regénérer le bail"}
          onClick={openLeaseRegenerateFormModal}
          requiresWrite
        />
        {[TenantLeaseType.NU, TenantLeaseType.MEUBLE].includes(
          tenant.lease_type,
        ) && (
          <CardAction
            icon={IconCalendarCheck}
            iconColor="orange"
            description={
              "Vous souhaitez mettre fin au bail de votre locataire ? Retrouvez le cadre légal, et nos modèles de courrier"
            }
            title={"Donnez congé à votre locataire"}
            onClick={() => {
              setTypeCourrier("conge");
              setIsAvenantOpen(true);
            }}
            requiresWrite
          />
        )}
        <CardAction
          icon={IconSend}
          iconColor="warning"
          description={
            "Sélectionnez un modèle de courrier à envoyer à un intervenant dans le dossier (locataire, syndic, caution, huissier, etc.)"
          }
          title={"Envoyer un courrier"}
          onClick={() => {
            setTypeCourrier("courrier");
            setIsAvenantOpen(true);
          }}
          requiresWrite
        />
        <CardAction
          icon={IconScale}
          iconColor="success"
          description={
            "Envoyer au locataire la régularisation annuelle entre la provision de charge payée et les charges dues (ne s'applique pas aux forfaits de charge)"
          }
          title={"Régularisation de charge"}
          onClick={() => {
            setTypeCourrier("courrier");
            setShortcut("regul_charge");
            setIsAvenantOpen(true);
          }}
          requiresWrite
          disabled={tenant.type_charge === ChargesType.FORFAIT}
          disabledTooltip={
            "Indisponible pour les baux au forfait de charge : la régularisation ne s'applique qu'aux baux avec provision sur charges."
          }
        />
        {TenantLeaseType.isHabitation(tenant.lease_type) && (
          <CardAction
            icon={IconFileText}
            iconColor="success"
            description={
              "Générer l'attestation de loyer CAF pré-remplie et l'envoyer par mail à votre locataire pour sa demande d'aide au logement."
            }
            title={"Attestation de loyer CAF"}
            onClick={() => setIsAttestationCafOpen(true)}
            requiresWrite
          />
        )}
        <CardAction
          icon={IconUserShield}
          iconColor="violet"
          description={
            "Sécurisez votre location et faites garantir le paiement du loyer à un garant qui va se porter caution."
          }
          title={"Acte de cautionnement"}
          onClick={() => {
            openCautionModal();
          }}
          requiresWrite
        />
        <CardAction
          icon={IconFilePlus}
          iconColor="info"
          description={
            "Générer un mandat de prélèvement pour le paiement des loyers et charges."
          }
          title={"Mandat de prélèvement"}
          onClick={() => {
            openMandatModal();
          }}
          requiresWrite
        />
      </div>
    </>
  );
}
